/**
 * Pure helpers for the document-style transcript editor (#9986).
 *
 * The transcript wire format is unchanged: lines of `Speaker: text`, `\n`-joined.
 * Speaker token is `Us` (mic audio, shown as "You"), `Them`, or a name; a line
 * with no `:` is text with an empty speaker. These helpers turn that format into
 * bubble HTML for a single contentEditable surface and serialize the edited DOM
 * back to the same wire format on save.
 */

import type { TranscriptVoice } from "./SpeakerMovePicker";

export interface TranscriptTurn {
  /** Raw speaker token as stored ("Us", "Them", or a name). Empty if none. */
  speaker: string;
  text: string;
}

/** Speaker tokens the recorder emits; used to gate paste re-bubbling. With
 * diarization (#11884) a token may carry a letter: "Us (A)", "Them (B)"; a
 * room (one microphone hearing everyone) labels "Speaker A" or "Speaker". */
function isKnownSpeaker(speaker: string): boolean {
  return /^((Us|Them)( \([A-Z]+\))?|Speaker( [A-Z]+)?)$/.test(speaker);
}

/** A turn read from the stored text, with where it sits there. */
export interface TranscriptLine extends TranscriptTurn {
  /** The line's 0-based index in the stored text split on newlines: blank
   * lines are skipped when parsing, so this is not its place in the list
   * (#13917). */
  line: number;
}

// The server's own rule for a line's speaker: up to 80 characters with no
// colon, then a colon and a space (`_TRANSCRIPT_LINE_LABEL`).
const LINE_LABEL = /^([^:\n]{1,80}): /;

/** The read-only view's lines (#13917). A speaker is recognised only by the
 * server's own rule, so a line the view offers to move is one the server
 * can name; `Note:text`, a colon inside the label or a label over 80
 * characters has no speaker. */
export function parseTranscriptLines(transcript: string): TranscriptLine[] {
  return transcript.split("\n").flatMap((line, index) => {
    if (!line.trim()) return [];
    const m = LINE_LABEL.exec(line);
    const speaker = m ? m[1].trim() : "";
    const text = m ? line.substring(m[0].length).trim() : line;
    return [{ speaker, text, line: index }];
  });
}

/**
 * Where a line went once the transcript changed under a refused move
 * (#13917): the index of the stored line carrying the same text (after its
 * `label: ` prefix) nearest `near`, or -1 when no line carries it. Never by
 * index alone: a line dropped above moves it up a slot.
 */
export function refindLine(
  stored: readonly string[],
  text: string,
  near: number
): number {
  let best = -1;
  stored.forEach((line, index) => {
    const m = LINE_LABEL.exec(line);
    if (!m || line.substring(m[0].length) !== text) return;
    if (best < 0 || Math.abs(index - near) < Math.abs(best - near)) {
      best = index;
    }
  });
  return best;
}

/** The run around line `at` (#13917): the indexes of the consecutive
 * lines by its speaker. Empty when `at` is no line. */
export function runAround(
  lines: readonly TranscriptTurn[],
  at: number
): number[] {
  if (at < 0 || at >= lines.length) return [];
  const speaker = lines[at].speaker;
  let start = at;
  let end = at;
  while (start > 0 && lines[start - 1].speaker === speaker) start -= 1;
  while (end < lines.length - 1 && lines[end + 1].speaker === speaker) end += 1;
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export function parseTranscript(transcript: string): TranscriptTurn[] {
  return transcript
    .split("\n")
    .filter((line) => line.trim())
    .map((line) => {
      const colonIdx = line.indexOf(":");
      const speaker = colonIdx > 0 ? line.substring(0, colonIdx).trim() : "";
      const text = colonIdx > 0 ? line.substring(colonIdx + 1).trim() : line;
      return { speaker, text };
    });
}

/** The stored line at `index` as the server names it (#13917): the label
 * before its first `": "` and the text after it. */
export function storedLineRef(
  transcript: string,
  index: number
): { index: number; label: string; text: string } {
  const raw = transcript.split("\n")[index] ?? "";
  const at = raw.indexOf(": ");
  return at < 0
    ? { index, label: "", text: raw }
    : { index, label: raw.slice(0, at), text: raw.slice(at + 2) };
}

export function serializeTurns(turns: TranscriptTurn[]): string {
  return turns
    .filter((turn) => turn.text.trim() !== "")
    .map((turn) =>
      turn.speaker ? `${turn.speaker}: ${turn.text.trim()}` : turn.text.trim()
    )
    .join("\n");
}

// Names that are the reader's own: speaker identification (#11884) writes the
// recorder's name onto their lines, and those still belong on the reader's
// side. Set once when the signed-in user is known.
let hostSpeakerNames: string[] = [];

function nameKey(name: string): string {
  return name.trim().split(/\s+/).join(" ").toLowerCase();
}

export function setHostSpeakerNames(
  names: (string | null | undefined)[]
): void {
  hostSpeakerNames = names.filter((n): n is string => !!n && !!n.trim());
}

// "Us" is how mic audio is stored. With speaker diarization (#11884) a shared
// mic splits into "Us (A)", "Us (B)": still the reader's side of the call, as
// is a line carrying the reader's own name. A component that knows the reader
// passes their names, so it renders again when they arrive.
export function isHostSideSpeaker(
  speaker: string,
  readerNames: readonly (string | null | undefined)[] = hostSpeakerNames
): boolean {
  if (speaker === "Us" || speaker.startsWith("Us (")) return true;
  const key = nameKey(speaker);
  return readerNames.some((n) => !!n && !!n.trim() && nameKey(n) === key);
}

// "Us" is shown as "You" to the reader; "Us (A)" as "You (A)"; a name as itself.
export function displaySpeaker(speaker: string): string {
  if (speaker === "Us" || speaker.startsWith("Us ("))
    return "You" + speaker.slice("Us".length);
  return speaker;
}

function escapeHtml(text: string): string {
  // Quotes must be escaped too: these values are interpolated into double-quoted
  // data-* attributes built as an innerHTML string, so an unescaped `"` in a
  // speaker token would let pasted/loaded text inject arbitrary attributes.
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Read a turn block's text, treating <br> and nested block elements (which a
// browser may create when the user presses Enter inside a bubble) as spaces so
// the words on either side don't silently fuse together on save. The per-bubble
// speaker-toggle control is skipped — it's chrome, not transcript text.
function readTurnText(el: HTMLElement): string {
  let text = "";
  for (const child of Array.from(el.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) {
      text += child.textContent ?? "";
    } else if (child.nodeName === "BR") {
      if (text && !text.endsWith(" ")) text += " ";
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      if (isBubbleControl(child as HTMLElement)) continue;
      if (text && !text.endsWith(" ")) text += " ";
      text += readTurnText(child as HTMLElement);
    }
  }
  return text;
}

// The hover affordance that flips a bubble between speakers. `contenteditable
// false` keeps it atomic inside the editable document; `data-flip` lets the
// click handler find it and lets readTurnText skip it on serialize.
const FLIP_CONTROL =
  '<span class="te-flip" data-flip contenteditable="false"' +
  ' role="button" aria-label="Switch speaker">⇄</span>';

// Where speakers carry names, the speaker's name over the bubble is the
// control that picks its speaker, as in the read view (#15238): a real
// element, first in the bubble, so it can take a click. `data-pick` is how
// the click handler finds it, and readTurnText skips it as it skips the flip.
function nameHtml(speaker: string): string {
  return (
    '<span class="te-name" data-pick contenteditable="false"' +
    ` role="button" aria-haspopup="listbox">${escapeHtml(
      displaySpeaker(speaker)
    )}</span>`
  );
}

function isBubbleControl(el: HTMLElement): boolean {
  return el.hasAttribute("data-flip") || el.hasAttribute("data-pick");
}

/** How a bubble's speaker is changed. The ⇄ flip where speakers are only
 * "Us" and "Them"; where they carry names (#13917) the flip would replace
 * a name with a side, so the name itself picks the speaker instead
 * (#15238). */
export interface BubbleOptions {
  control: "flip" | "pick";
}

const WITH_FLIP: BubbleOptions = { control: "flip" };

/** The pick mode's name, first in the bubble; or the flip, last. */
function controlsHtml(
  speaker: string,
  text: string,
  options: BubbleOptions
): string {
  if (options.control === "flip") return `${text}${FLIP_CONTROL}`;
  return `${speaker ? nameHtml(speaker) : ""}${text}`;
}

/** Keep a pick-mode bubble's name in order (#15238): exactly one, its first
 * child, reading its speaker as the transcript shows it; none for a bubble
 * with no speaker. A merge that folds a second bubble in brings that one's
 * name into the text, and that copy goes. */
function keepName(bubble: HTMLElement, speaker: string): void {
  const first = bubble.firstChild;
  const keep =
    speaker && first instanceof HTMLElement && first.hasAttribute("data-pick")
      ? first
      : null;
  for (const name of Array.from(bubble.querySelectorAll("[data-pick]"))) {
    if (name !== keep) name.remove();
  }
  if (!speaker) return;
  if (!keep) {
    const tmp = document.createElement("template");
    tmp.innerHTML = nameHtml(speaker);
    if (tmp.content.firstChild) bubble.prepend(tmp.content.firstChild);
    return;
  }
  const shown = displaySpeaker(speaker);
  if (keep.textContent !== shown) keep.textContent = shown;
}

/** One bubble block for a turn. `data-speaker` lets the DOM round-trip on save. */
export function turnToBubbleHtml(
  turn: TranscriptTurn,
  options: BubbleOptions = WITH_FLIP
): string {
  const side = isHostSideSpeaker(turn.speaker)
    ? "te-bubble-us"
    : "te-bubble-them";
  const label = turn.speaker ? escapeHtml(displaySpeaker(turn.speaker)) : "";
  return (
    `<div class="te-bubble ${side}" data-speaker="${escapeHtml(turn.speaker)}"` +
    ` data-label="${label}">${controlsHtml(
      turn.speaker,
      escapeHtml(turn.text),
      options
    )}</div>`
  );
}

/** Set a bubble's speaker in place (#15238): what Save writes, the label it
 * shows (its name, where it has one), and the side it sits on, as the flip
 * decides a side. */
export function setBubbleSpeaker(bubble: HTMLElement, speaker: string): void {
  const us = isHostSideSpeaker(speaker);
  bubble.setAttribute("data-speaker", speaker);
  bubble.setAttribute("data-label", displaySpeaker(speaker));
  bubble.classList.toggle("te-bubble-us", us);
  bubble.classList.toggle("te-bubble-them", !us);
  const first = bubble.firstChild;
  if (first instanceof HTMLElement && first.hasAttribute("data-pick")) {
    first.textContent = displaySpeaker(speaker);
  }
}

/** Toggle a bubble between the two sides (Us <-> Them) in place. A lettered
 * or named speaker flips to the plain label of the other side. */
export function flipBubbleSpeaker(bubble: HTMLElement): void {
  const current = bubble.getAttribute("data-speaker") ?? "";
  setBubbleSpeaker(bubble, isHostSideSpeaker(current) ? "Them" : "Us");
}

/**
 * Ensure every top-level block is a well-formed bubble.
 *
 * When the user presses Enter the browser makes a fresh block that lacks our
 * markup, so a new line wouldn't look like a bubble or carry a speaker-toggle
 * until a save re-rendered it. Run on input, this re-applies the bubble class,
 * side, inherited speaker, label, and the toggle control to any block missing
 * them. It only sets attributes/classes and appends a missing control (never
 * restructures), so it's safe to run on every keystroke without moving the caret.
 */
export function normalizeBubbles(
  container: HTMLElement,
  options: BubbleOptions = WITH_FLIP
): void {
  let lastSpeaker = "Us";
  for (const node of Array.from(container.childNodes)) {
    if (node.nodeType !== Node.ELEMENT_NODE) continue;
    const el = node as HTMLElement;
    if (el.tagName === "BR" || isBubbleControl(el)) continue;
    const attr = el.getAttribute("data-speaker");
    const speaker = attr === null ? lastSpeaker : attr;
    lastSpeaker = speaker;
    el.setAttribute("data-speaker", speaker);
    el.setAttribute("data-label", displaySpeaker(speaker));
    el.classList.add("te-bubble");
    el.classList.toggle("te-bubble-us", isHostSideSpeaker(speaker));
    el.classList.toggle("te-bubble-them", !isHostSideSpeaker(speaker));
    if (options.control === "pick") {
      keepName(el, speaker);
    } else if (!el.querySelector("[data-flip]")) {
      const tmp = document.createElement("template");
      tmp.innerHTML = FLIP_CONTROL;
      if (tmp.content.firstChild) el.appendChild(tmp.content.firstChild);
    }
  }
}

export function turnsToHtml(
  turns: TranscriptTurn[],
  options: BubbleOptions = WITH_FLIP
): string {
  return turns.map((turn) => turnToBubbleHtml(turn, options)).join("");
}

/**
 * Serialize the editor's contentEditable DOM back to the wire format.
 *
 * Reads each top-level block's `data-speaker` and text. A block without the
 * attribute (e.g. a browser-created split on Enter, or a merge artifact) inherits
 * the previous block's speaker — this is also what makes a select-and-delete that
 * merges two turns keep the earlier speaker, since the browser keeps the first
 * block (with its attribute) and folds the survivor's text into it.
 */
export function serializeEditor(container: HTMLElement): string {
  const turns: TranscriptTurn[] = [];
  let lastSpeaker = "";
  for (const node of Array.from(container.childNodes)) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      if (el.tagName === "BR" || isBubbleControl(el)) continue;
      const attr = el.getAttribute("data-speaker");
      const speaker = attr === null ? lastSpeaker : attr;
      lastSpeaker = speaker;
      turns.push({ speaker, text: readTurnText(el) });
    } else if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent ?? "";
      if (text.trim() === "") continue;
      // Stray text node (browser quirk): fold into the current/previous turn.
      if (turns.length > 0) turns[turns.length - 1].text += text;
      else turns.push({ speaker: lastSpeaker, text });
    }
  }
  return serializeTurns(turns);
}

/**
 * Serialize just a selected range to the wire format, keeping speaker prefixes.
 *
 * This is what makes "highlight a chunk, copy it, paste it elsewhere" retain the
 * us-vs-them attribution: the speaker labels are CSS pseudo-elements (not text),
 * so a raw clipboard copy would lose them. Cloning the range preserves the
 * partially-selected bubble blocks with their `data-speaker`, so we re-serialize
 * to `Speaker: text` lines — which paste then re-bubbles. A selection inside a
 * single bubble has no block boundary and serializes to plain (un-prefixed) text.
 */
export function serializeRange(range: Range): string {
  const holder = document.createElement("div");
  holder.appendChild(range.cloneContents());
  return serializeEditor(holder);
}

/**
 * Decide how a pasted chunk should be inserted.
 *
 * `speakerLines: true` -> the chunk is itself a transcript (e.g. copied from
 * another meeting via the Copy button, which emits `Us:`/`Them:` lines); insert
 * its turns as new bubbles. Otherwise it's plain text to drop into the current
 * turn at the caret. Single-line `Word: text` is only treated as a transcript
 * when the speaker is a known recorder token, so pasting prose like "Note: call
 * back" doesn't get mis-attributed to a "Note" speaker.
 */
export function classifyPaste(text: string): {
  speakerLines: boolean;
  turns: TranscriptTurn[];
} {
  const turns = parseTranscript(text);
  const speakerLines =
    turns.some((turn) => isKnownSpeaker(turn.speaker)) ||
    (turns.length > 1 && turns.every((turn) => turn.speaker !== ""));
  return { speakerLines, turns };
}

/** The speakers the editor's bubbles carry now, in the order they first
 * speak, with how many bubbles each has (#15238): the pick lists these, not
 * the saved text's, since the edit may have added, removed or re-attributed
 * lines. A bubble with no speaker is no voice. */
export function editorSpeakers(container: HTMLElement): TranscriptVoice[] {
  const counts = new Map<string, number>();
  for (const node of Array.from(container.children)) {
    const el = node as HTMLElement;
    if (isBubbleControl(el)) continue;
    const speaker = el.getAttribute("data-speaker");
    if (!speaker) continue;
    counts.set(speaker, (counts.get(speaker) ?? 0) + 1);
  }
  return Array.from(counts, ([label, lines]) => ({ label, lines }));
}

// A placeholder's letters, as the server hands them out: A to Z, then AA,
// AB, ... (`_placeholder_letters`).
function letterAt(index: number): string {
  const abc = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  if (index < abc.length) return abc[index];
  const rest = index - abc.length;
  return abc[Math.floor(rest / abc.length)] + abc[rest % abc.length];
}

function letterIndex(letter: string): number {
  const at = (c: string) => c.charCodeAt(0) - 65;
  return letter.length === 1
    ? at(letter)
    : 26 + at(letter) * 26 + at(letter[1]);
}

const ROOM_LABEL = /^Speaker ([A-Z]{1,2})$/;
// A room recording's labels: a bare "Speaker" (one voice) or a lettered one.
const ROOM_SHAPE = /^Speaker( [A-Z]{1,2})?$/;
const SIDE_LABEL = /^(Us|Them) \(([A-Z]{1,2})\)$/;

/**
 * The label "New speaker" mints for a bubble now carrying `current`
 * (#15238 requirement 4): a lettered placeholder in the recording's own
 * scheme (`Speaker X` in a room or phone recording, else `Us (X)` on the
 * reader's side and `Them (X)` on the far side), at the first letter that
 * no bubble in the editor carries and that is not below the server's floor.
 * The floor is the letter of `newSpeakerFor[current]`, which skips letters a
 * kept voice holds; for a label without one, the highest suggestion in the
 * same scheme and side; A when there is none.
 */
export function newSpeakerLabel(
  current: string,
  editorLabels: readonly string[],
  newSpeakerFor: Readonly<Record<string, string>>
): string {
  const suggestions = Object.values(newSpeakerFor);
  const room = [...editorLabels, ...suggestions].some((label) =>
    ROOM_SHAPE.test(label)
  );
  const side = isHostSideSpeaker(current) ? "Us" : "Them";
  const labelFor = (letter: string): string =>
    room ? `Speaker ${letter}` : `${side} (${letter})`;
  // The letter of a label in this scheme and side, or null.
  const letterOf = (label: string): string | null => {
    if (room) return ROOM_LABEL.exec(label)?.[1] ?? null;
    const m = SIDE_LABEL.exec(label);
    return m && m[1] === side ? m[2] : null;
  };
  const own = newSpeakerFor[current];
  const ownLetter = own === undefined ? null : letterOf(own);
  const floor =
    ownLetter !== null
      ? letterIndex(ownLetter)
      : Math.max(
          0,
          ...suggestions.flatMap((label) => {
            const letter = letterOf(label);
            return letter === null ? [] : [letterIndex(letter)];
          })
        );
  const taken = new Set(editorLabels);
  let index = floor;
  while (taken.has(labelFor(letterAt(index)))) index += 1;
  return labelFor(letterAt(index));
}
